import React, {useCallback, useEffect, useState} from "react";
import AlertExecution from "@app/model/alert/AlertExecution";
import PriceComparisonAlertExecutionNotification from "@app/model/alert/PriceComparisonAlertExecutionNotification";
import moment from "moment";
import isEmpty from "lodash/isEmpty";
import PriceComparisonAlertExecutionNotificationRowFragment
    from "@app/site/secured/alert-execution/fragment/price-comparison/PriceComparisonAlertExecutionNotificationRowFragment";
import AlertUtilities from "@app/site/secured/alert/util/AlertUtilities";
import _orderBy from "lodash/orderBy";
import PriceComparisonAlertRulePricePoint from "@app/model/alert/PriceComparisonAlertRulePricePoint";
import PriceComparisonAlertExecutionNotificationOutput
    from "@app/model/alert/output/PriceComparisonAlertExecutionNotificationOutput";
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';

type NotificationsGroup = {
    type: 'NEXT_WEEK' | 'MONTH',
    month?: string,
    notificationOutputs: PriceComparisonAlertExecutionNotificationOutput[]
}

const PriceComparisonAlertExecutionMostImportantNotificationsViewFragment: React.FunctionComponent<{
    alertExecution: AlertExecution,
    notificationOutputs: PriceComparisonAlertExecutionNotificationOutput[],
    marketStateDetailsModal: any,
    isInDrawer?: boolean
}> = ({alertExecution, notificationOutputs, marketStateDetailsModal, isInDrawer = false}) => {

    const [groups, setGroups] = useState<NotificationsGroup[]>();

    const existsInGroup = useCallback((groups: NotificationsGroup[], groupType: 'NEXT_WEEK' | 'MONTH', month?: string) => {
        return groups.find(g => g.type === groupType && g.month === month)
    }, [notificationOutputs]);

    const isWithinNextWeek = useCallback((notification: PriceComparisonAlertExecutionNotification) => {
        const untilDate = moment().add(7, 'days');
        return moment(notification.statDate).isSameOrBefore(untilDate)
    }, [notificationOutputs]);

    useEffect(() => {
        const groups: NotificationsGroup[] = []

        notificationOutputs.forEach(notificationOutput => {
            notificationOutput.current.difference = AlertUtilities.calculateDifference(notificationOutput.current);

            if (isWithinNextWeek(notificationOutput.current)) {

                let nextWeekGroup = existsInGroup(groups, 'NEXT_WEEK');
                if(!nextWeekGroup) {
                    nextWeekGroup= {
                        type: 'NEXT_WEEK',
                        notificationOutputs: []
                    }
                    groups.push(nextWeekGroup)
                }
                nextWeekGroup.notificationOutputs.push(notificationOutput)
            } else {
                const month = moment(notificationOutput.current.statDate).format('MMMM \'YY');

                let monthGroup = existsInGroup(groups, 'MONTH', month);
                if(!monthGroup) {
                    monthGroup = {
                        type: 'MONTH',
                        month: month,
                        notificationOutputs: []
                    }
                    groups.push(monthGroup)
                }

                monthGroup.notificationOutputs.push(notificationOutput)
            }
        });

        groups.forEach(g => {
            g.notificationOutputs = _orderBy(g.notificationOutputs, n => Math.abs(n.current.difference!), 'desc').splice(0, 5)
        })

        setGroups(groups)
    }, [notificationOutputs]);


    return !isEmpty(groups) ? groups!.map((group, index) => <div key={`idx_${index}`}
        className={`mt-8 ${isInDrawer ? 'bg-gray-50' : 'bg-white'} rounded-xl space-y-16 sm:space-y-20 lg:mx-0 p-4 sm:p-10`}>
        <div>
            <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold leading-6 text-gray-900">
                    {group.type === 'NEXT_WEEK' ? 'Next Week' : group.month}
                </h2>
            </div>
            <div className="mt-1 flow-root">
                <div className="overflow-x-auto">
                    <div className="inline-block min-w-full py-2 align-middle">
                        <table className="min-w-full divide-y divide-gray-300 text-gray-500">
                            <thead>
                            <tr>
                                <th scope="col"
                                    className="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Date
                                </th>
                                <th scope="col"
                                    className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Your
                                    Price
                                </th>
                                <th scope="col"
                                    className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    {alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MEDIAN && "Median"}
                                    {alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MIN && "Least Expensive"}
                                    {alertExecution.alertOutput.priceComparisonAlertRule.pricePoint === PriceComparisonAlertRulePricePoint.MAX && "Most Expensive"}
                                </th>
                                {alertExecution.alertOutput.priceComparisonAlertRule.pricePoint !== PriceComparisonAlertRulePricePoint.MEDIAN && <th scope="col"
                                                                                                                                                     className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Competitor
                                </th>}
                                <th scope="col"
                                    className="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">
                                    Diff.
                                </th>
                                <th scope="col"
                                    className="relative whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-0">
                                </th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                            {group.notificationOutputs.map((notificationOutput) => <PriceComparisonAlertExecutionNotificationRowFragment
                                key={notificationOutput.current.id} notificationOutput={notificationOutput} alertExecution={alertExecution}
                                marketStateDetailsModal={marketStateDetailsModal} isInDrawer={isInDrawer}/>)}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>) : <TablePlaceholder/>
}


export default PriceComparisonAlertExecutionMostImportantNotificationsViewFragment;